import { RequestFailureBanner } from '../RequestFailureBanner'
import { StudyBuilderRequestFailures, evaluationFailureSections } from './StudyBuilderRequestFailures'
/**
 * What: Streamlined single-page study setup panel consolidating form rendering and preflight actions.
 * Transport / data-flow: Composes modular Polaris cards bound to the unified form hook and builder state.
 *
 */
import { StudyBuilderSections } from './StudyBuilderSections'
import { shopifyComponentCopy } from '@testfeed/product-language'
import type { useShopifyStudyBuilder } from '../../hooks/study-builder/index.js'
import type { PageActionName } from '../../page-declaration'
import type { page } from '../../pages/study-builder.page'
import type { ActionButtonLifecycle } from '../ActionButton'
import { SectionBlocks, type ClientComponents } from './SectionBlocks'
import { resolveWorkingCues } from './workingPhrase'
import { StudySetupActionBar } from './StudySetupActionBar.js'
import { ShopifyPageSkeleton } from '../ShopifyPageSkeleton.js'
import { SummaryAudience, SummaryProduct, SummaryShoppers, SummaryTesting } from './SummaryBlocks'
import { StudyBuilderLayout } from './StudyBuilderLayout'
import { useMemo, type ReactNode } from 'react'
import { usePageNotice } from '../../hooks/usePageNotice'
import type { StudyBuilderSidebarRead } from '../../hooks/study-builder/useStudyBuilderSidebarQuery'

/** The summary sidebar has no editable blocks, so a reported edit is a bug; fail loud rather than silently swallow it. */
function summaryReadOnly(): never {
  throw new Error('STUDY_BUILDER_SUMMARY_IS_READ_ONLY')
}

export type UnifiedStudySetupPanelBuilder = Pick<
  ReturnType<typeof useShopifyStudyBuilder>,
  | 'evaluation'
  | 'pageState'
  | 'visit'
  | 'permissions'
  | 'navigation'
  | 'handleOpenProductPicker'
  | 'handleImageUpload'
  | 'upload'
  | 'edit'
  | 'formError'
  | 'requestFailures'
  | 'handleRunStudyFromBuilder'
  | 'handleDiscard'
  | 'handleSaveDraft'
  | 'handleLoadSavedVersion'
  | 'isDirty'
  | 'readiness'
> & {
  /** Only what the action buttons read: `button(action)` for the page's declared actions. */
  lifecycle: ActionButtonLifecycle<PageActionName<typeof page>>
}

export function UnifiedStudySetupPanel({
  builder,
  bookingUrl,
  failureNotice,
  onBack,
  sidebar,
}: {
  builder: UnifiedStudySetupPanelBuilder
  bookingUrl?: string
  failureNotice?: ReactNode
  /** The route's guarded navigation, also available when initial evaluation cannot render the editor. */
  onBack?: () => void
  sidebar: StudyBuilderSidebarRead
}) {
  const pendingValues = useMemo(
    () => new Map(builder.evaluation.pendingEdits.map((edit) => [edit.key, edit.value])),
    [builder.evaluation.pendingEdits],
  )
  const isAuthoringDisabled = builder.permissions.structureDisabled
  const isTextDisabled = builder.permissions.textDisabled

  // No automatic notice reads a study-builder fact (the picker is always visible). The panel derives none from the
  // projection; a future notice declares the facts it needs in page-rules and the Worker sends them in the payload.
  usePageNotice({
    page: 'study-builder',
    state: builder.pageState,
    visit: builder.visit,
    facts: {},
    actions: { 'choose-product': builder.handleOpenProductPicker },
  })

  if (!builder.evaluation.projection) {
    if (builder.formError) return <StudyBuilderRequestFailures builder={builder} location="form" onBack={onBack} />
    return <ShopifyPageSkeleton accessibilityLabel={shopifyComponentCopy.studyBuilderPanel.loadingSetup007} />
  }
  const { readiness } = builder.evaluation.projection
  const hasBlockingReadFailure = builder.requestFailures.some((failure) => failure.blocksSubmission)
  // The Worker decided `enabled` and sent the words for every state. The page knows only the in-flight browser facts (a
  // read that failed, a check running, a proposal awaiting confirm); it swaps to that state's words and lowers Submit for
  // it. It picks no wording and re-derives no readiness.
  const readFailure = hasBlockingReadFailure || Boolean(builder.evaluation.sourceError)
  const checking = Boolean(builder.evaluation.checking)
  const submitWords = readFailure ? readiness.onReadFailure : checking ? readiness.whileChecking : readiness.submit
  const canSubmit = readiness.enabled && !readFailure && !checking
  // Each section that owns an edit in flight shows the working cue, with the Worker's own word for that edit. The page
  // selects nothing: `resolveWorkingCues` matches each pending edit key to the block the Worker already labelled.
  const sections = builder.evaluation.projection?.sections ?? []
  const workingCues = resolveWorkingCues(
    sections,
    builder.evaluation.pendingEdits,
    Boolean(builder.evaluation.checking),
  )
  const locks = { text: isTextDisabled, structure: isAuthoringDisabled }
  const failureSections = evaluationFailureSections(builder)
  const sectionFailure = (sectionId: string) => {
    if (!failureSections.has(sectionId)) return undefined
    return <StudyBuilderRequestFailures builder={builder} location="form" sectionId={sectionId} />
  }
  const clientActions = {
    'choose-product': () => {
      void builder.handleOpenProductPicker()
    },
  }
  // The summary sidebar's browser-rendered pieces: the Worker places each by a `client` block; the app draws them from the
  // Worker-sent summary and separately checked sidebar presentation.
  const summary = builder.evaluation.projection.summary
  const summaryComponents: ClientComponents = {
    'summary-product': () => <SummaryProduct product={summary.product} />,
    'summary-testing': () => <SummaryTesting testing={summary.testing} />,
    'summary-audience': () => <SummaryAudience audience={summary.audience} />,
    'summary-shoppers': () => (
      <SummaryShoppers shoppers={summary.shoppers} sidebar={sidebar} offlineMessage={readiness.onReadFailure.help} />
    ),
  }
  return (
    <StudyBuilderLayout
      bookingUrl={bookingUrl}
      summary={
        <SectionBlocks
          section={builder.evaluation.projection.summarySection}
          onEdit={summaryReadOnly}
          stale={false}
          clientComponents={summaryComponents}
        />
      }
    >
      <s-stack direction="block" gap="large-200">
        {failureNotice}
        {builder.evaluation.projection.pageAlert ? (
          <RequestFailureBanner message={builder.evaluation.projection.pageAlert.text} />
        ) : null}
        <StudyBuilderRequestFailures builder={builder} location="form" />
        <StudyBuilderSections
          sections={sections}
          renderBlocks={(section) => (
            <SectionBlocks
              section={section}
              onEdit={builder.edit}
              onUpload={builder.handleImageUpload}
              upload={builder.upload}
              stale={hasBlockingReadFailure}
              workingPhrase={workingCues.get(section.id) ?? null}
              requestFailure={sectionFailure(section.id)}
              locks={locks}
              pending={pendingValues}
              clientActions={clientActions}
            />
          )}
        />

        {/* A Submit refusal shows here, by the Submit bar, where the merchant acted — not at the page top. */}
        <StudyBuilderRequestFailures builder={builder} location="submit" />
        <StudySetupActionBar
          canSubmit={canSubmit}
          readiness={submitWords}
          lifecycle={builder.lifecycle}
          onSubmit={builder.handleRunStudyFromBuilder}
        />
      </s-stack>
    </StudyBuilderLayout>
  )
}
